import ApiClient from "./apiClient"
import AuthenticationController from "./authenticationController"
import ExcelController from "./excelController"
import { Project } from "../f19types/project";
import { TableSource } from "../f19types/tablesource";
import { Job, JobMessagePriority, JobStatus } from "../f19types/job";
import StorageController from "./storageController";

export default class SyncController {

    private readonly pollInterval: number = 500;

    private client: ApiClient;

    private projects: HTMLSelectElement;
    private sources: HTMLSelectElement;
    private start: HTMLButtonElement;
    private cancel: HTMLButtonElement;
    private sourcesContainer: HTMLElement;
    private sourceNameContainer: HTMLElement;
    private createSource: HTMLInputElement;
    private sourceName: HTMLInputElement;
    private processWorksheets: HTMLInputElement;
    private processRanges: HTMLInputElement;
    private processDesigns: HTMLSelectElement;
    private status: HTMLElement;
    private runningJobId: number|null = null;
 
    private selectedProjectId: string = "";
    private selectedSourceId: string = "";

    public constructor(private container: HTMLElement, eventHost: HTMLElement, private auth: AuthenticationController) {
        eventHost.addEventListener(AuthenticationController.LoginEvent, () => this.authenticated())
        eventHost.addEventListener(AuthenticationController.LogoutEvent, () => this.updateUI())
        this.sourcesContainer = container.querySelector(".js-sources-container") as HTMLElement;
        this.sources = this.sourcesContainer.querySelector(".js-sources") as HTMLSelectElement;
        this.sourceNameContainer = container.querySelector(".js-source-name-container") as HTMLElement;
        this.sourceName = container.querySelector(".js-source-name") as HTMLInputElement;
        this.createSource = container.querySelector(".js-create-source") as HTMLInputElement;
        this.status = container.querySelector(".js-status") as HTMLElement;

        this.createSource.onchange = () => this.updateUI();
        this.projects = container.querySelector(".js-projects") as HTMLSelectElement;
        this.start = container.querySelector(".js-start") as HTMLButtonElement;
        this.cancel = container.querySelector(".js-cancel") as HTMLButtonElement;
        this.projects.onchange = () => {
            this.selectedProjectId = this.projects.value;
            this.updateSources();
        }
        this.sources.onchange = () => { 
            this.selectedSourceId = this.sources.value;
            this.updateUI();
        }

        this.processWorksheets = container.querySelector(".js-process-worksheets") as HTMLInputElement;
        this.processWorksheets.onchange = () => this.updateUI();
        this.processRanges = container.querySelector(".js-process-ranges") as HTMLInputElement;
        this.processRanges.onchange = () => this.updateUI();
        this.processDesigns = container.querySelector(".js-process-designs") as HTMLSelectElement;
        this.processDesigns.onchange = () => this.updateUI();

        this.start.onclick = () => this.startSync();
        this.cancel.onclick = () => this.cancelSync();
  
        // Remember settings used last time we synced this file
        this.selectedProjectId = StorageController.get("selectedProjectId") ?? "";
        this.selectedSourceId = StorageController.get("selectedSourceId") ?? "";
        this.sourceName.value = StorageController.get("sourceName") ?? ExcelController.fileName();
        this.processWorksheets.checked = StorageController.get("processWorksheets") == "true";
        this.processRanges.checked = StorageController.get("processRanges") != "false";
        this.processDesigns.value = StorageController.get("processDesigns") ?? "-1";
  
        if (this.auth.authenticated) {
            this.authenticated();
        } else {
            this.updateUI();
        }

        this.client = new ApiClient(auth);
    }

    private async authenticated() {
        await this.updateProjects();
        await this.updateSources();
        this.updateUI();
    }

    private async updateProjects() {
        this.projects.innerHTML = '';

        const defaultOption = document.createElement("option");
        defaultOption.text = "Loading...";
        defaultOption.disabled = true;
        defaultOption.selected = true;
        defaultOption.value = "";
        this.projects.disabled = true;
        this.projects.add(defaultOption);

        let projects = await this.client.getJson<Project[]>("project");
        if (projects == null || projects.length == 0) {
            defaultOption.selected = true;
            defaultOption.text = "No projects found";
            return;
        }
        projects.sort((a, b) => a.name.localeCompare(b.name));
        
        let selectedFound = false;
        for (let project of projects) {
            const newoption = document.createElement("option");
            newoption.text = project.name;
            newoption.value = project.id;
            if (project.isFrozen) {
                newoption.disabled = true;
            }
            this.projects.add(newoption);
            if (project.id == this.selectedProjectId) {
                defaultOption.selected = false;
                newoption.selected = true;
                selectedFound = true;
            }
        }
        if (!selectedFound) {
            defaultOption.selected = true;
        }
        this.projects.disabled = false;
        defaultOption.text = "Choose project...";
    }

    private async updateSources() {
        this.sources.innerHTML = '';
        this.sources.disabled = true;
        this.sourcesContainer.hidden = false;
        this.sourceNameContainer.hidden = true;
        this.start.disabled = true;

        const defaultOption = document.createElement("option");
        defaultOption.text = "Loading...";
        defaultOption.disabled = true;
        defaultOption.selected = true;
        defaultOption.value = "";
        this.sources.add(defaultOption);

        let sources: TableSource[]|null = null;
        if (this.selectedProjectId) {
            sources = await this.client.getJson<TableSource[]>(`tablesourcefile/list/project/${this.selectedProjectId}`);
        }

        if (sources == null || sources.length == 0) {
            defaultOption.text = "No sources found";
            this.createSource.checked = true;
            this.createSource.disabled = true;
            this.createSource.dispatchEvent(new Event("change"));
            return;
        }
        this.sources.disabled = false;
        this.createSource.checked = false;
        this.createSource.disabled = false;
        
        sources.sort((a, b) => a.name.localeCompare(b.name));
        let selectedFound = false;
        let nameMatch: HTMLOptionElement|null = null;
        for (let source of sources) {
            const newOption = document.createElement("option");
            newOption.value = source.id;
            newOption.text = source.name;
            this.sources.add(newOption);
            if (source.id == this.selectedSourceId) {
                newOption.selected = true;
                defaultOption.selected = false;
                selectedFound = true;
            }
            if (this.sourceName.value == source.name) {
                nameMatch = newOption;
            }
        }
        if (!selectedFound) {
            if (nameMatch) {
                nameMatch.selected = true;
                this.selectedSourceId = nameMatch.value;
            } else {
                defaultOption.selected = true;
            }
        }        
        defaultOption.text = "Choose source...";
        // Triggers updateUI later
        this.sources.dispatchEvent(new Event("change")) 
    }

    private updateUI() {
        if (this.auth.authenticated) {
            this.container.hidden = false;
        } else {
            this.container.hidden = true;
        }
        if (this.createSource.checked) {
            this.sourceNameContainer.hidden = false;
            this.sourcesContainer.hidden = true;
        } else {
            this.sourceNameContainer.hidden = true;
            this.sourcesContainer.hidden = false;
        }
        let syncEnabled = !!this.selectedProjectId;
        if (this.createSource.checked) {
            syncEnabled = syncEnabled && !!this.sourceName.value;
        } else {
            syncEnabled = syncEnabled && !!this.selectedSourceId
        }
        syncEnabled = syncEnabled && (this.processRanges.checked || this.processWorksheets.checked);
        syncEnabled = syncEnabled && !this.runningJobId;

        if (this.runningJobId) {
            this.projects.disabled = true;
            this.sources.disabled = true;
            this.sourceName.disabled = true;
            this.createSource.disabled = true;
            this.processDesigns.disabled = true;
            this.processWorksheets.disabled = true;
            this.processRanges.disabled = true;
            this.cancel.hidden = false;
        } else {
            this.projects.disabled = this.projects.options.length == 1;
            this.sources.disabled = false;
            this.sourceName.disabled = false;
            this.createSource.disabled = false;
            this.processDesigns.disabled = false;
            this.processWorksheets.disabled = false;
            this.processRanges.disabled = false;
            this.cancel.hidden = true;
        }

        // We can start sync if we have selected project, source file and ranges
        this.start.disabled = !syncEnabled;

    }

    private async startSync() {
        this.start.disabled = true;
        const blob = await ExcelController.saveAsBlob();
        if (!blob) {
            return;
        }
        let url = `tablesync/start/project/${this.selectedProjectId}?`;
        if (this.createSource.checked) {
            url += `sourceName=${encodeURIComponent(this.sourceName.value)}`;
        } else {
            url += `sourceId=${this.selectedSourceId}`;
        }
        if (!this.processRanges.checked) {
            url += `&extractNamedRanges=false`;
        }
        if (this.processWorksheets.checked) {
            url += `&extractWorksheets=true`;
        }
        switch (this.processDesigns.value) {
            case "0":
                url += `&extractDesigns=false`;
                break;
            case "1":
                url += `&extractDesigns=true`;
                break;
            case "-1":
            default:
                // Nothing, default is auto
        }

        this.runningJobId = await this.client.postBlobAsStream<number>(url, blob);
        if (!this.runningJobId) {
            this.updateStatus("Failed to start job", "problem");
        } else {
            this.updateStatus(`Started job ${this.runningJobId}`, "neutral");
            setTimeout(() => this.getJobStatus(), this.pollInterval);
        }
        this.updateUI();
    }

    private async getJobStatus() {
        const jobStatus = await this.client.getJson<Job>(`tablesync/status/project/${this.selectedProjectId}/job/${this.runningJobId}`);
        if (jobStatus == null) {
            this.updateStatus(`Connection lost, visit dashboard for status`, `warning`);
            this.runningJobId = null;
            this.updateUI();
            return;
        }
        if (jobStatus.status & JobStatus.Waiting) {
            if (jobStatus.status & JobStatus.ForUser) {
                // Dashboard url
                const dash = `${this.auth.url}cms/tables/tablesync/job/${jobStatus.id}`
                this.updateStatus(`Ambiguity detected, visit <a href="${dash}" target=_blank>dashboard</a> to resolve`, `warning`, true);
                this.runningJobId = null;
                this.updateUI();
            } else {
                this.updateStatus(`Waiting for our turn...`, `neutral`);
                setTimeout(() => this.getJobStatus(), this.pollInterval);
            }
        } else if (jobStatus.status & JobStatus.Active) {
            let message = jobStatus.messages.at(-1);
            let status = "Synchronising";
            if (message?.text) {
                status += `: ${message.text}`
            }
            this.updateStatus(status, `neutral`);
            this.cancel.disabled = true;
            setTimeout(() => this.getJobStatus(), this.pollInterval);
        } else if (jobStatus.status & JobStatus.Done) {
            if (jobStatus.status & JobStatus.WithErrors) {
                let error = jobStatus.messages.find(x => x.priority == JobMessagePriority.Error);
                this.updateStatus(`Done with error: ${error?.text ?? '?'}`, `problem`);
            } else if (jobStatus.status & JobStatus.WithWarnings) {
                let warning = jobStatus.messages.find(x => x.priority == JobMessagePriority.Warning);
                this.updateStatus(`Done with warning: ${warning?.text ?? '?'}`, `warning`);
            } else if (jobStatus.status & JobStatus.WithNoChanges) {
                this.updateStatus(`Done (no changes)`, "neutral");
            } else {
                this.updateStatus(`Done!`, "success");
            }

            // We saved by source name, now convert to source id for next time
            if (this.createSource.checked) {
                await this.updateSources();
            }

            // Remember settings for next time
            StorageController.set("selectedProjectId", this.selectedProjectId);
            StorageController.set("selectedSourceId", this.selectedSourceId);
            StorageController.set("sourceName", this.sourceName.value);
            StorageController.set("processWorksheets", this.processWorksheets.checked.toString());
            StorageController.set("processRanges", this.processRanges.checked.toString());
            StorageController.set("processDesigns", this.processDesigns.value);
            
            this.runningJobId = null;
            this.updateUI();
        }
    }

    private async cancelSync() {
        const success = await this.client.post<boolean>(`tablesync/cancel/project/${this.selectedProjectId}/job/${this.runningJobId}`);
        if (success) {
            this.updateStatus(`Cancelled job ${this.runningJobId}`, "neutral");
        } else {
            this.updateStatus(`Unable to cancel job ${this.runningJobId}`, "problem");
        }
        this.runningJobId = null;
        this.updateUI();
    }

    private updateStatus(message: string, type: "problem"|"neutral"|"warning"|"success", html: boolean = false) {
        if (message) {
            this.status.hidden = false;
            this.status.setAttribute("status", type);
            const msg = this.status.querySelector("span") as HTMLElement;
            if (html) {
                msg.innerHTML = message;
            } else {
                msg.innerText = message;
            }
        } else {
            this.status.hidden = true;
        }

    }
}